import cx from "classnames";
import { useEffect, useRef, useState } from "react";
import { appstr } from "@coreapp/utils/appstr";

import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  isNullish,
  Modal
} from "@theorgchartdev/storybook";
import { DocumentExportContent } from "./DocumentExport";
import { ExcelContent } from "./ExcelExport";
import { GetWSMgr } from "@/Utils/StateLib";
import { ImageExportContent } from "./ImageExport";

type Props = {
  mode: Nullable<ExportModalMode>;
  onClose: () => void;
};

export type DisabledData = {
  isDisabled: boolean;
  tooltip?: string;
};

export const ExportModal = (props: Props) => {
  const handleRef = useRef<ExportHandleRef>(null);
  const [isExporting, setIsExporting] = useState(false);
  // This is a map that will keep track of different reasons to disable the Export button
  // If any value in this object is false, the button will be disabled
  const [isDisabledMap, setDisabledMap] = useState<Record<string, DisabledData>>({});

  useEffect(() => {
    if (isNullish(props.mode)) {
      return;
    }

    setIsExporting(false);
  }, [props.mode]);

  if (isNullish(props.mode)) {
    return;
  }

  const handleExport = async () => {
    const wsMgr = GetWSMgr();
    if (isNullish(props.mode) || isNullish(wsMgr)) {
      return;
    }

    try {
      setIsExporting(true);
      const isCompleted = await handleRef.current?.handleExport();
      if (isCompleted) {
        const exportFormat = ExportFormatProperty[props.mode];
        if (exportFormat) {
          wsMgr.GetUsageTracker()?.ReportEvent("chart_mode.export.exported", {
            export_format: exportFormat
          });
        }
        props.onClose();
      }
    } finally {
      setIsExporting(false);
    }
  };

  const hasPreviewVisible = [
    ExportModalMode.JPG,
    ExportModalMode.PNG,
    ExportModalMode.SVG,
    ExportModalMode.PDF,
    ExportModalMode.PPT
  ].includes(props.mode);
  const activeDisabled = Object.values(isDisabledMap).find(value => value.isDisabled);

  const setDisabled = (key: string, value: DisabledData) => {
    setDisabledMap(prev => ({ ...prev, [key]: value }));
  };

  return (
    <CentralizedModalContainer show={true}>
      <Modal
        id="export-modal"
        centerScreen
        fullScreen={hasPreviewVisible}
        minWidth="large"
        maxWidth="large"
        onClose={props.onClose}
        show={true}
      >
        <DialogTitle divider={true} onClose={props.onClose}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"arrow-up-right-from-square"} />
            {appstr("exporto")} {appstr(ExportModalLabel[props.mode])}
          </span>
        </DialogTitle>
        <DialogContent
          customContentClass={cx("ocn-chart-explorer-content", {
            "oc-export-modal-content": !hasPreviewVisible
          })}
        >
          {props.mode === ExportModalMode.EXCEL && (
            <ExcelContent handleRef={handleRef} setDisabled={setDisabled} />
          )}
          {[ExportModalMode.JPG, ExportModalMode.PNG, ExportModalMode.SVG].includes(props.mode) && (
            <ImageExportContent mode={props.mode} handleRef={handleRef} setDisabled={setDisabled} />
          )}
          {[ExportModalMode.PDF, ExportModalMode.PPT].includes(props.mode) && (
            <DocumentExportContent
              mode={props.mode}
              handleRef={handleRef}
              setDisabled={setDisabled}
            />
          )}
        </DialogContent>
        <DialogActions>
          <DialogActions.Tertiary dataTestId="export-modal-action-cancel" onClick={props.onClose}>
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            tooltipProps={activeDisabled?.tooltip ? { message: activeDisabled.tooltip } : undefined}
            dataTestId="export-modal-action-export"
            disabled={activeDisabled?.isDisabled}
            loading={isExporting}
            onClick={handleExport}
          >
            {appstr("Export")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

export type ExportHandleRef = {
  handleExport: () => Promise<boolean>;
};

export enum ExportModalMode {
  EXCEL,
  JPG,
  SVG,
  PNG,
  PPT,
  PDF
}

// Map to localization keys
const ExportModalLabel: Record<ExportModalMode, string> = {
  [ExportModalMode.EXCEL]: "Excel Spreadsheet",
  [ExportModalMode.JPG]: "JPG Image",
  [ExportModalMode.SVG]: "SVG Image",
  [ExportModalMode.PNG]: "PNG Image",
  [ExportModalMode.PPT]: "Powerpoint document",
  [ExportModalMode.PDF]: "PDF Document"
};

const ExportFormatProperty: Partial<Record<ExportModalMode, string>> = {
  [ExportModalMode.EXCEL]: "excel",
  [ExportModalMode.JPG]: "jpg",
  [ExportModalMode.PNG]: "png",
  [ExportModalMode.SVG]: "svg",
  [ExportModalMode.PPT]: "ppt",
  [ExportModalMode.PDF]: "pdf"
};
